import type { AdType } from '@hprofits/shared';
import { getAllowedPricingModelsForAdType } from '@hprofits/shared';
import type { SelectOption } from '@hprofits/ui';

import { tOptional } from '@/i18n';

import type { PricingModelValue } from './columnEnums';
import { DELIVERIES, PRICING_MODELS } from './columnEnums';

/** CPC is never offered — it is not used by any ad type on this platform. */
const ALWAYS_HIDDEN_MODELS: ReadonlySet<PricingModelValue> = new Set(['CPC'] as const);

/**
 * Options for `getPricingModelSelectOptions` — visibility of the CPA choice depends on both the
 * viewer's role and the row's own current value.
 */
export type PricingModelSelectOptionsParams = {
  /** CPA is admin-only; a non-admin never sees it as a selectable option. */
  isAdmin: boolean;
  /** The row's own current pricingModel value, always kept in the option list — see below. */
  currentValue: PricingModelValue | null;
};

/**
 * Returns selectOptions for the pricingModel column, filtered by ad type, role, and the row's
 * own current value.
 *
 * CPC is hidden unconditionally (not used by any ad type). CPA is offered only to admins.
 * `currentValue` is always included in the returned options even when it would otherwise be
 * filtered out — a non-admin opening a rule an admin already set to CPA must still get a select
 * that contains its own current value, otherwise it is missing from the option list and silently
 * blanks or corrupts on save. Callers must additionally render the cell read-only when
 * `!isAdmin && currentValue === 'CPA'` — this function only controls the option list, not
 * interactivity.
 *
 * Falls back to all non-hidden models only when adType is absent (e.g. a new pending group whose
 * type has not resolved yet). A *known* ad type carrying an empty policy keeps its empty list — the
 * backend rejects every model for it (`Allowed: none`), so offering the full set would just walk the
 * user into a 422; the cell is left effectively read-only on `currentValue` instead (#910).
 * Option value equals the enum string (domain-aligned, not a numeric index).
 */
export const getPricingModelSelectOptions = (
  adType: AdType | string | null | undefined,
  { isAdmin, currentValue }: PricingModelSelectOptionsParams,
): ReadonlyArray<SelectOption> => {
  const allowed = getAllowedPricingModelsForAdType(adType);
  // Truthiness rather than `hasValue` — mirrors `getAllowedPricingModelsForAdType`'s own `!adType`
  // guard exactly, so '' cannot mean "known type" here and "unknown type" there.
  const base: ReadonlyArray<PricingModelValue> = adType ? allowed : PRICING_MODELS;
  const visible = base.filter((m) => !ALWAYS_HIDDEN_MODELS.has(m) && (m !== 'CPA' || isAdmin));
  const models = currentValue && !visible.includes(currentValue) ? [...visible, currentValue] : visible;
  return models.map((m) => ({
    value: m,
    label: m,
    description: tOptional(`modules.campaigns.hints.adGroup.pricingModels.${m}`),
  }));
};

/** Select options for the delivery column — value equals the enum string. */
export const DELIVERY_SELECT_OPTIONS: ReadonlyArray<SelectOption> = DELIVERIES.map((d) => ({
  value: d,
  label: d,
  // Module-scope constant: a resolved `tOptional` would pin the description to the language the
  // chunk loaded in, so the read happens on access instead.
  get description() {
    return tOptional(`modules.campaigns.hints.adGroup.deliveries.${d}`);
  },
}));
